import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ShieldCheck, ShieldAlert } from "lucide-react";
import { useCompany } from "../../../context/CompanyContext";
import {
  getApVendors,
  getReconciliation,
} from "../../../Services/subledgerService";
import "./subledger.css";

const money = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function APVendors() {
  const { companyId } = useCompany();
  const nav = useNavigate();

  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [rec, setRec] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      setLoading(true);
      try {
        const [vendors, r] = await Promise.all([
          getApVendors(companyId),
          getReconciliation(companyId),
        ]);
        setRows(vendors);
        setRec(r?.ap || null);
      } catch (e) {
        console.error(e);
        alert(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      (r.vendor_name || "").toLowerCase().includes(term)
    );
  }, [q, rows]);

  const recOk = rec?.status === "PASS";

  return (
    <div className="sl-shell">
      <div className="sl-row">
        <div>
          <h2 className="sl-title">Accounts Payable</h2>
          <p className="sl-sub">
            Journal-driven vendor balances and open bills.
          </p>
        </div>

        <div className="sl-row" style={{ justifyContent: "flex-end" }}>
          <span className={`sl-chip ${recOk ? "ok" : "danger"}`}>
            {recOk ? <ShieldCheck size={14} /> : <ShieldAlert size={14} />}
            Reconciliation: {recOk ? "PASS" : "FAIL"}
          </span>

          <button className="sl-btn" onClick={() => nav("/approvals")}>
            Approvals
          </button>
          <button
            className="sl-btn sl-btnPrimary"
            onClick={() => nav("/ap/allocations")}
          >
            Allocation Workspace
          </button>
        </div>
      </div>

      <div className="sl-card" style={{ marginTop: 12 }}>
        <div className="sl-row">
          <div className="sl-search">
            <Search size={16} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search vendor name…"
            />
          </div>
        </div>

        <div className="sl-tableWrap" style={{ marginTop: 12 }}>
          <table className="sl-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th className="num">AP Balance</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(10)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={3} style={{ opacity: 0.7 }}>
                      Loading…
                    </td>
                  </tr>
                ))
              ) : filtered.length ? (
                filtered.map((r) => (
                  <tr key={r.vendor_id}>
                    <td>
                      <button
                        className="sl-link"
                        onClick={() => nav(`/subledger/ap/${r.vendor_id}`)}
                      >
                        {r.vendor_name ||
                          String(r.vendor_id).slice(0, 8).toUpperCase()}
                      </button>
                    </td>
                    <td className="num">
                      <strong>₦{money(r.ap_balance)}</strong>
                    </td>
                    <td>
                      {Number(r.ap_balance || 0) > 0 ? (
                        <span className="sl-chip neutral">Open</span>
                      ) : (
                        <span className="sl-chip ok">Prepay/Zero</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="sl-empty">
                    <strong>No AP yet</strong>
                    Post bill/payment journals to A/P control to populate AP.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {rec && (
          <div style={{ marginTop: 12, opacity: 0.85, fontSize: 13 }}>
            Control balance: <strong>₦{money(rec.ap_control_balance)}</strong> •
            Open items total: <strong>₦{money(rec.ap_open_items_total)}</strong>
          </div>
        )}
      </div>
    </div>
  );
}
